import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SpecraButton, SpecraSegmentedControl } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { useAttachmentPreview } from '../../../hooks/useAttachmentPreview';
import { artifactClient, documentsClient } from '../../../lib/connect/clients';
import { OriginalPdfSource } from './OriginalPdfSource';
import { normalized, plainQuote, SourceText } from './SourceText';
/** Uploaded artifacts and generated Office documents have distinct source stores. */ export function SourceDocumentDialog({ documentId, name, onClose, quote: citedQuote = '', sourceAnchor }) {
    // One place: every locator below (saved text, Office text, original PDF)
    // searches text without Markdown, so the quote is searched without it too.
    const quote = useMemo(()=>plainQuote(citedQuote), [
        citedQuote
    ]);
    const cacheScope = useId();
    const [showOriginal, setShowOriginal] = useState(false);
    const [located, setLocated] = useState(null);
    const source = useAttachmentPreview({
        attachmentId: documentId,
        cacheScope
    });
    const [seekCitation, setSeekCitation] = useState(true);
    const [offsets, setOffsets] = useState([
        0
    ]);
    const offset = offsets[offsets.length - 1];
    const officeSource = source.isError && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(documentId) && ConnectError.from(source.error).code === Code.NotFound;
    const office = useQuery({
        queryKey: [
            'citation-office-context',
            documentId,
            cacheScope,
            offset,
            seekCitation,
            quote
        ],
        queryFn: async ()=>{
            let sourceOffset = offset;
            let result = await documentsClient.getDocumentText({
                documentId,
                offset: sourceOffset,
                limit: 12000
            });
            // Locate a later cited passage before opening its saved-text window.
            if (quote && seekCitation) {
                const needle = normalized(quote).value.trim();
                for(let pages = 1; pages < 80 && !normalized(plainQuote(result.text)).value.includes(needle); pages++){
                    const next = sourceOffset + result.characterCount;
                    if (!result.characterCount || next >= result.totalCharacters) break;
                    sourceOffset = next;
                    result = await documentsClient.getDocumentText({
                        documentId,
                        offset: sourceOffset,
                        limit: 12000
                    });
                }
            }
            return {
                ...result,
                sourceOffset
            };
        },
        enabled: officeSource,
        retry: false,
        staleTime: 0,
        gcTime: 0
    });
    const extracted = useQuery({
        queryKey: [
            'citation-source-text',
            documentId,
            cacheScope
        ],
        queryFn: async ()=>{
            let bytes;
            try {
                const response = await artifactClient.getArtifactChunk({
                    artifactId: documentId,
                    chunkKind: 'processed_markdown'
                });
                bytes = new Uint8Array(response.data);
            } catch (error) {
                if (ConnectError.from(error).code !== Code.NotFound || !source.data) throw error;
                const original = await fetch(source.data);
                const type = original.headers.get('content-type') ?? '';
                if (!original.ok || !/^(text\/|application\/json(?:;|$))/.test(type)) throw error;
                bytes = new Uint8Array(await original.arrayBuffer());
            }
            const hash = [
                ...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))
            ].map((n)=>n.toString(16).padStart(2, '0')).join('');
            return {
                text: new TextDecoder('utf-8', {
                    ignoreBOM: true,
                    fatal: true
                }).decode(bytes),
                hash
            };
        },
        enabled: !!source.data,
        retry: false,
        staleTime: 0,
        gcTime: 0
    });
    const metadata = useQuery({
        queryKey: [
            'citation-source-label',
            documentId,
            cacheScope
        ],
        queryFn: ()=>artifactClient.getArtifact({
                artifactId: documentId
            }),
        enabled: !!source.data,
        retry: false
    });
    const anchored = extracted.data && sourceAnchor ? verifiedSourceSection(extracted.data.text, extracted.data.hash, quote, sourceAnchor) : null;
    const loading = source.isLoading || officeSource && office.isPending;
    const failed = source.isError && !officeSource || officeSource && office.isError;
    const sourceOffset = office.data?.sourceOffset ?? offset;
    const nextOffset = sourceOffset + (office.data?.characterCount ?? 0);
    const pdf = metadata.data?.artifact?.mimeType === 'application/pdf' || /\.pdf$/i.test(metadata.data?.artifact?.filename || name);
    const cited = quote ? /*#__PURE__*/ _jsxs("figure", {
        className: "space-y-1 rounded-md border border-border bg-muted/40 px-3 py-2",
        "data-testid": Testids.assistant.sourceQuote,
        children: [
            /*#__PURE__*/ _jsx("figcaption", {
                className: "text-[length:var(--fs-ui-caption)] font-medium text-muted-foreground",
                children: "Cited passage"
            }),
            /*#__PURE__*/ _jsx("blockquote", {
                className: "line-clamp-4 text-sm text-foreground",
                children: quote
            }),
            located !== null ? /*#__PURE__*/ _jsx("output", {
                className: "block text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: located ? 'Cited passage highlighted below.' : 'Not found word-for-word — showing the full source.'
            }) : null
        ]
    }) : null;
    return /*#__PURE__*/ _jsx(Sheet, {
        open: true,
        onOpenChange: (open)=>{
            if (!open) onClose();
        },
        children: /*#__PURE__*/ _jsxs(SheetContent, {
            className: "flex w-full flex-col gap-3 overflow-hidden sm:max-w-2xl",
            "data-testid": Testids.assistant.sourcePanel,
            children: [
                /*#__PURE__*/ _jsxs(SheetHeader, {
                    className: "space-y-1 pr-6",
                    children: [
                        /*#__PURE__*/ _jsx(SheetTitle, {
                            className: "line-clamp-2 break-words text-base",
                            children: metadata.data?.artifact?.displayTitle || name || 'Source document'
                        }),
                        /*#__PURE__*/ _jsxs(SheetDescription, {
                            className: "flex min-w-0 items-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "min-w-0 truncate",
                                    children: name
                                }),
                                source.data ? /*#__PURE__*/ _jsx("a", {
                                    href: source.data,
                                    download: name,
                                    "aria-label": "Download original source",
                                    title: "Download original source",
                                    className: "shrink-0 rounded-sm p-0.5 text-muted-foreground hover:text-foreground",
                                    children: /*#__PURE__*/ _jsx(Download, {
                                        className: "size-3.5",
                                        "aria-hidden": "true"
                                    })
                                }) : null
                            ]
                        })
                    ]
                }),
                loading ? /*#__PURE__*/ _jsx(SourceSkeleton, {}) : null,
                failed ? /*#__PURE__*/ _jsxs("div", {
                    role: "alert",
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            children: "This source could not be opened. It may be unavailable or outside your access."
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            onClick: ()=>void (officeSource ? office.refetch() : source.refetch()),
                            children: "Retry source"
                        })
                    ]
                }) : officeSource && office.data ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        cited,
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                            children: "Current saved document"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "min-h-0 flex-1 space-y-4 overflow-y-auto",
                            children: [
                                /*#__PURE__*/ _jsx(SourceText, {
                                    text: office.data.text || 'This saved document has no text content.',
                                    quote: quote,
                                    onLocated: setLocated
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-3",
                                    children: [
                                        sourceOffset > 0 ? /*#__PURE__*/ _jsx(SpecraButton, {
                                            variant: "secondary",
                                            onClick: ()=>{
                                                setSeekCitation(false);
                                                setOffsets((previous)=>previous.length > 1 ? previous.slice(0, -1) : [
                                                        Math.max(0, sourceOffset - 12000)
                                                    ]);
                                            },
                                            children: "Previous"
                                        }) : null,
                                        office.data.characterCount > 0 && sourceOffset + office.data.characterCount < office.data.totalCharacters ? /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx("p", {
                                                    children: "More source content is available."
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                    variant: "secondary",
                                                    onClick: ()=>{
                                                        setSeekCitation(false);
                                                        setOffsets((previous)=>[
                                                                ...previous,
                                                                nextOffset
                                                            ]);
                                                    },
                                                    children: "Next"
                                                })
                                            ]
                                        }) : null
                                    ]
                                })
                            ]
                        })
                    ]
                }) : source.data ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        pdf ? /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                            label: "Source view",
                            size: "sm",
                            value: showOriginal ? 'original' : 'readable',
                            onValueChange: (view)=>setShowOriginal(view === 'original'),
                            options: [
                                {
                                    value: 'readable',
                                    label: 'Readable text'
                                },
                                {
                                    value: 'original',
                                    label: 'Original PDF'
                                }
                            ],
                            className: "self-start"
                        }) : null,
                        showOriginal && pdf ? /*#__PURE__*/ _jsx(OriginalPdfSource, {
                            documentId: documentId,
                            quote: quote
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                cited,
                                /*#__PURE__*/ _jsx("div", {
                                    className: "min-h-0 flex-1 space-y-4 overflow-y-auto",
                                    children: extracted.isPending ? /*#__PURE__*/ _jsx(SourceSkeleton, {
                                        body: true
                                    }) : extracted.data ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            sourceAnchor && !anchored ? /*#__PURE__*/ _jsx("p", {
                                                role: "alert",
                                                className: "text-sm text-muted-foreground",
                                                children: "The citation position could not be verified against this source. Showing the quote match instead."
                                            }) : null,
                                            anchored ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    /*#__PURE__*/ _jsxs("h3", {
                                                        children: [
                                                            "Cited section · lines ",
                                                            anchored.startLine,
                                                            "–",
                                                            anchored.endLine
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx(SourceText, {
                                                        text: anchored.text,
                                                        quote: quote,
                                                        onLocated: setLocated
                                                    }),
                                                    /*#__PURE__*/ _jsx("h3", {
                                                        children: "Full document"
                                                    })
                                                ]
                                            }) : null,
                                            /*#__PURE__*/ _jsx(SourceText, {
                                                text: extracted.data.text,
                                                quote: anchored ? '' : quote,
                                                onLocated: anchored ? undefined : setLocated
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsx("p", {
                                        children: "Readable text is unavailable for this file. Download the original to inspect it."
                                    })
                                })
                            ]
                        })
                    ]
                }) : null
            ]
        })
    });
}
export function verifiedSourceSection(text, hash, quote, anchor) {
    const characters = Array.from(text);
    if (hash !== anchor.hash || !Number.isSafeInteger(anchor.start) || !Number.isSafeInteger(anchor.end) || anchor.start < 0 || anchor.end <= anchor.start || anchor.end > characters.length) return null;
    const section = characters.slice(anchor.start, anchor.end).join('');
    // Fail closed when the model picked a range that does not contain its quote.
    if (!normalized(plainQuote(section)).value.includes(normalized(plainQuote(quote)).value.trim())) return null;
    const startLine = characters.slice(0, anchor.start).filter((c)=>c === '\n').length + 1;
    return {
        text: section,
        startLine,
        endLine: startLine + section.split('\n').length - 1
    };
}
/** Placeholder in the final layout's shape, so the panel does not jump on load. */ function SourceSkeleton({ body = false }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        "aria-busy": "true",
        children: [
            /*#__PURE__*/ _jsx("output", {
                className: "sr-only",
                children: "Loading source document…"
            }),
            body ? null : /*#__PURE__*/ _jsx("div", {
                className: "h-16 rounded-md animate-shimmer"
            }),
            [
                100,
                92,
                96,
                70,
                88,
                60
            ].map((width)=>/*#__PURE__*/ _jsx("div", {
                    className: "h-3 rounded-sm animate-shimmer",
                    style: {
                        width: `${width}%`
                    }
                }, width))
        ]
    });
}
